import Container from "../components/Container";
import { NavLink } from "react-router-dom";
import Icon from "../components/Icon";
import Show from "../components/Show";
import { useState } from "react";
import { useMediaQuery } from 'react-responsive';
import { Button } from "primereact/button";
import { useAuthContext } from "../contexts/AuthContext";
import packageJson from "../../package.json";
import {
    analysisTypeFromRoute,
    trackAnalysisMenuOpened,
    trackAnalysisNavTapped,
} from "../analytics";

interface MenuItem {
    label: string;
    icon: string;
    route?: string;
    open?: boolean;
    items?: MenuItem[];
}

export default function SideMenu() {
    const isTablet = useMediaQuery({ minWidth: 569, maxWidth: 768 });
    const isDesktop = useMediaQuery({ minWidth: 769 });
    const isMobile = useMediaQuery({ maxWidth: 568 });

    const auth = useAuthContext();

    const [items, setItems] = useState<MenuItem[]>([
        {
            label: 'Home',
            icon: 'pi-th-large',
            route: '/'
        },
        {
            label: 'Carteira',
            icon: 'pi-wallet',
            route: '/wallet'
        },
        {
            label: 'Eventos',
            icon: 'pi-arrow-right-arrow-left',
            route: '/events'
        },
        {
            label: 'Análises',
            icon: 'pi-chart-line',
            open: false as boolean | undefined,
            items: [
                {
                    label: 'Rentabilidade',
                    icon: '',
                    route: '/analysis/rentability'
                },
                {
                    label: 'Evolução',
                    icon: '',
                    route: '/analysis/evolution'
                },
                {
                    label: 'Proventos',
                    icon: '',
                    route: '/analysis/earnings'
                },
                {
                    label: 'Projeção',
                    icon: '',
                    route: '/analysis/projection'
                },
                {
                    label: 'Fundos imobiliários',
                    icon: '',
                    route: '/analysis/fiis'
                }
            ]
        }
    ]);

    return (
        <Container flexColumn fullWidth relative>
            <Container className="flex-1" flexColumn={isDesktop || isTablet} center={isMobile}>
                {items.map(item => {
                    return (
                        <Show
                            key={item.label}
                            when={!!item.route}
                            fallback={
                                <div className="side-menu__dropdown">
                                    <div
                                        className={[
                                            "side-menu__item no-underline p-3 border-round flex gap-2 align-items-center my-1 cursor-pointer",
                                            item.items?.some(subitem => window.location.pathname.includes(subitem.route!)) && !item.open ? "bg-primary" : ""
                                        ].join(" ")}
                                        onClick={() => {
                                            if (item.label === 'Análises' && !item.open) {
                                                trackAnalysisMenuOpened();
                                            }
                                            setItems(oldItems => oldItems.map(i => i.label === item.label ? { ...i, open: !i.open } : i));
                                        }}
                                    >
                                        <Icon name={item.icon} />

                                        <Container between fullWidth className="align-items-center">
                                            <span className="side-menu__label">{item.label}</span>
                                            <Icon name={item.open ? "pi-chevron-up" : "pi-chevron-down"} className="side-menu__dropdown__icon--desktop" />
                                            <Icon name={item.open ? "pi-chevron-left" : "pi-chevron-right"} className="side-menu__dropdown__icon--tablet" />
                                            <Icon name={item.open ? "pi-chevron-down" : "pi-chevron-up"} className="side-menu__dropdown__icon--mobile" />
                                        </Container>
                                    </div>
                                    {
                                        item.open && (
                                            <div className={`side-menu__subitems ${isTablet ? "fadeinleft" : isMobile ? "fadeindown" : "fadein"} animation-duration-500`}>
                                                {item.items?.map(subitem => (
                                                    <NavLink
                                                        key={subitem.label}
                                                        to={subitem.route!}
                                                        onClick={() => {
                                                            const analysisType = analysisTypeFromRoute(subitem.route!);
                                                            if (analysisType) {
                                                                trackAnalysisNavTapped(analysisType);
                                                            }
                                                        }}
                                                        className={({ isActive }) =>
                                                            [
                                                                isActive ? "bg-primary" : "",
                                                                "side-menu__item no-underline p-3 border-round flex gap-2 align-items-center my-1"
                                                            ].join(" ")
                                                        }
                                                    >
                                                        <Icon name={subitem.icon} />
                                                        <span className="side-menu__label" style={{ fontSize: "16px" }}>{subitem.label}</span>
                                                    </NavLink>
                                                ))}
                                            </div>
                                        )
                                    }
                                </div>
                            }
                        >
                            <NavLink
                                to={item.route!}
                                className={({ isActive }) =>
                                    [
                                        isActive ? "bg-primary" : "",
                                        "side-menu__item no-underline p-3 border-round flex gap-2 align-items-center my-1"
                                    ].join(" ")
                                }
                            >
                                <Icon name={item.icon} />
                                <span className="side-menu__label">{item.label}</span>
                            </NavLink>
                        </Show>
                    )
                })}
            </Container>

            <Show when={isDesktop || isTablet}>
                <Container flexColumn className="mb-2">
                    <NavLink
                        to={"/profile"}
                        className={({ isActive }) =>
                            [
                                isActive ? "bg-primary" : "",
                                "side-menu__item no-underline p-3 border-round flex gap-2 align-items-center my-1"
                            ].join(" ")
                        }
                    >
                        <Icon name="pi-user" />
                        <span className="side-menu__label">{auth.user?.name.split(" ")[0]}</span>
                    </NavLink>

                    <Button
                        onClick={() => {
                            void auth.logout();
                        }}
                        className="side-menu__item no-underline p-3 border-round flex gap-2 align-items-center my-1 bg-white border-0 hover:bg-primary hover:text-white"
                    >
                        <Icon name="pi-sign-out" title="Sair" />
                        <span className="side-menu__label">Sair</span>
                    </Button>

                    <span className="side-menu__label text-xs text-right">Versão {packageJson.version}</span>
                </Container>
            </Show>
        </Container>
    )
}
